import React, { FC, FocusEvent, ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../reducers';

import { Chip, FormControlLabel, FormGroup, Checkbox, TextField, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { JwtRole } from '@thingslog/repositories/src/jwt/JWT';
import { UserDto } from '@thingslog/repositories';
import { UserDtoRole } from '@thingslog/repositories/src/user/UserDto';
import { useForm } from 'react-hook-form';

const AddUserModal: FC<AddModalProps> = (props: AddModalProps) => {
  const { onCloseModal, onCreateUser } = props;
  const { t } = useTranslation();
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const companyName = useSelector((state: ReduxState) =>
    state.company.name ? state.company.name : state.auth.companyName
  );

  const currentUserRoles = useSelector((state: ReduxState) => state.auth.roles);

  const language = useSelector((state: ReduxState) => state.auth.language);

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    trigger,
    formState: { isSubmitting, isValid, errors, touchedFields },
  } = useForm<AddUserProps>({
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      roles: [],
      dashboardEnabled: false,
      isContactPerson: false,
      language: language!,
      companyName: companyName!,
    },
  });

  const assignRole = (role: UserDtoRole): void => {
    let currentRoles = watch('roles');
    if (currentRoles.includes(role)) {
      currentRoles = currentRoles.filter((x: string) => x != role);
    } else {
      currentRoles.push(role);
    }
    setValue('roles', currentRoles);
  };

  const checkAdminRights = (adminRole: string): boolean => {
    let result = currentUserRoles!.some((element: JwtRole) => {
      if (element.authority === adminRole) return true;
      return false;
    });

    return result;
  };

  const submitForm = (props: AddUserProps): void => {
    const { confirmPassword: _, ...userProps } = props;

    const user: UserDto = { username: userProps.email, ...userProps };

    onCreateUser(user);
    onCloseModal();
  };

  const passwordRules: PasswordRule[] = [
    {
      key: 'minLength',
      label: (t('password_min_length') as string) || 'Password must be at least 10 characters long',
    },
    {
      key: 'uppercase',
      label:
        (t('password_uppercase') as string) ||
        'Password must contain at least one uppercase letter',
    },
    {
      key: 'lowercase',
      label:
        (t('password_lowercase') as string) ||
        'Password must contain at least one lowercase letter',
    },
    {
      key: 'number',
      label: (t('password_number') as string) || 'Password must contain at least one number',
    },
    {
      key: 'specialChar',
      label:
        (t('password_special_char') as string) ||
        'Password must contain at least one special character',
    },
  ];

  const passwordField = register('password', {
    validate: {
      minLength: (value: string) =>
        value.length >= 10 ||
        (t('password_min_length') as string) ||
        'Password must be at least 10 characters long',
      uppercase: (value: string) =>
        /[A-Z]/.test(value) ||
        (t('password_uppercase') as string) ||
        'Password must contain at least one uppercase letter',
      lowercase: (value: string) =>
        /[a-z]/.test(value) ||
        (t('password_lowercase') as string) ||
        'Password must contain at least one lowercase letter',
      number: (value: string) =>
        /\d/.test(value) ||
        (t('password_number') as string) ||
        'Password must contain at least one number',
      specialChar: (value: string) =>
        /[!@#$%^&*(),.?":{}|<>]/.test(value) ||
        (t('password_special_char') as string) ||
        'Password must contain at least one special character',
    },
  });
  const passwordErrorTypes = errors.password?.types as Record<string, string> | undefined;

  const getPasswordHelperText = (): ReactNode => {
    if (!isPasswordFocused && !touchedFields.password) {
      return '';
    }

    const rulesToRender: PasswordRule[] = isPasswordFocused
      ? passwordRules
      : passwordRules.filter((rule: PasswordRule) => !!passwordErrorTypes?.[rule.key]);

    if (rulesToRender.length === 0) {
      return '';
    }

    return (
      <div className="flex flex-col">
        {rulesToRender.map((rule: PasswordRule) => (
          <span
            key={rule.key}
            style={{
              color: isPasswordFocused
                ? passwordErrorTypes?.[rule.key]
                  ? '#d32f2f'
                  : 'rgba(0, 0, 0, 0.87)'
                : '#d32f2f',
            }}
          >
            {rule.label}
          </span>
        ))}
      </div>
    );
  };

  return (
    <form className="flex flex-col gap-7 mt-6" onSubmit={handleSubmit(submitForm)}>
      <TextField
        {...register('email', { required: true })}
        label={t('users_table_email')}
        type="email"
        size="small"
        fullWidth
        error={!!errors.email}
        helperText={errors.email ? t('sensors_and_network_config_required_field_error') : ''}
      />
      <div className="flex gap-7">
        <TextField
          {...register('firstName')}
          label={t('users_table_first_name')}
          size="small"
          fullWidth
        />
        <TextField
          {...register('lastName')}
          label={t('users_table_last_name')}
          size="small"
          fullWidth
        />
      </div>
      <TextField
        {...passwordField}
        label={t('password')}
        type="password"
        size="small"
        fullWidth
        error={
          !isPasswordFocused &&
          !!touchedFields.password &&
          passwordRules.some((rule: PasswordRule) => !!passwordErrorTypes?.[rule.key])
        }
        helperText={getPasswordHelperText()}
        onFocus={(): void => {
          setIsPasswordFocused(true);
          void trigger('password');
        }}
        onBlur={(event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
          passwordField.onBlur(event);
          setIsPasswordFocused(false);
        }}
      />
      <TextField
        {...register('confirmPassword', {
          required: true,
          validate: (value: string) =>
            value === watch('password') ||
            (t('password_mismatch') as string) ||
            'Passwords do not match',
        })}
        label={t('confirm_password')}
        type="password"
        size="small"
        fullWidth
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword ? errors.confirmPassword.message : ''}
      />
      <div className="flex justify-center gap-2 mx-2">
        <Chip
          color="primary"
          size="small"
          variant="filled"
          icon={watch('roles').includes('USER') ? <CheckCircleOutlineIcon /> : undefined}
          onClick={(): void => assignRole('USER')}
          label={t('role_user')}
        />
        <Chip
          color="primary"
          size="small"
          variant="filled"
          icon={watch('roles').includes('PUBLIC') ? <CheckCircleOutlineIcon /> : undefined}
          onClick={(): void => assignRole('PUBLIC')}
          label={t('role_public')}
        />
        <Chip
          color="primary"
          size="small"
          variant="filled"
          icon={watch('roles').includes('ADMIN') ? <CheckCircleOutlineIcon /> : undefined}
          onClick={(): void => assignRole('ADMIN')}
          label={t('role_admin')}
        />
        <Chip
          color="primary"
          size="small"
          variant="filled"
          icon={watch('roles').includes('BILLING') ? <CheckCircleOutlineIcon /> : undefined}
          onClick={(): void => assignRole('BILLING')}
          label={t('role_billing')}
        />
        <Chip
          color="primary"
          size="small"
          variant="filled"
          icon={watch('roles').includes('RESTRICTED') ? <CheckCircleOutlineIcon /> : undefined}
          onClick={(): void => assignRole('RESTRICTED')}
          label={t('role_restricted')}
        />
        {checkAdminRights('ROLE_SUPER_ADMIN') && (
          <Chip
            size="small"
            color="primary"
            variant="filled"
            icon={
              watch('roles').includes('SUPER_ADMIN') ? <CheckCircleOutlineIcon /> : <span></span>
            }
            onClick={(): void => assignRole('SUPER_ADMIN')}
            label={t('role_super_admin')}
          />
        )}
      </div>
      <div className="flex justify-evenly">
        <FormGroup>
          <FormControlLabel
            control={<Checkbox {...register('dashboardEnabled')} />}
            label={t('users_is_dashboard_enabled')}
          />
        </FormGroup>
        {checkAdminRights('ROLE_SUPER_ADMIN') && (
          <FormGroup>
            <FormControlLabel
              control={<Checkbox {...register('isContactPerson')} />}
              label={t('users_is_contact_person')}
            />
          </FormGroup>
        )}
      </div>

      <Button
        fullWidth
        type="submit"
        variant="contained"
        disabled={isSubmitting || !isValid}
        disableElevation
      >
        {t('capital_create_btn')}
      </Button>

      <div className="m-auto flex flex-col gap-2">
        <span>{t('for_company')}:</span>
        <div className="text-center font-bold">{companyName}</div>
      </div>
    </form>
  );
};

interface AddModalProps {
  onCloseModal: () => void;
  onCreateUser: (user: UserDto) => void;
}

interface AddUserProps {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  roles: UserDtoRole[];
  language: string;
  dashboardEnabled: boolean;
  isContactPerson: boolean;
}

interface PasswordRule {
  key: string;
  label: string;
}

export default AddUserModal;
